import { useMemo, useState } from 'react';
import { useAsyncEffect } from 'ahooks';
import { fetchGSDOptions } from '@intelligence/services/topcharts';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import type { ICommonOption } from '@intelligence/types/common';

// segment别名映射
const SEGMENT_NAME_MAP: Record<string, string> = {};

// device分类排序顺序
const DEVICE_CATEGORIES_SORT_KEYS = ['playstation', 'xbox', 'nintendo'];

// device分类别名映射
const DEVICE_CATEGORY_NAME_MAP: Record<string, string> = {};

// device分类于device别名映射的映射
const DEVICE_NAME_MAP_MAP: Record<string, Record<string, string>> = {
  nintendo: {
    'wii u': 'Nintendo Wii U',
  },
};

const formatSegmentOptions = (segments: any[]): ICommonOption[] => {
  if (!Array.isArray(segments)) return [];
  return segments.map((segment) => {
    const segmentValue = segment?.segment;
    const segmentValueLowerCase = segmentValue?.toLowerCase();
    const segmentName = SEGMENT_NAME_MAP[segmentValueLowerCase] || segment?.segment_display;
    return { value: segmentValue, text: segmentName };
  });
};

const formatDeviceOptions = (devices: any[]): ICommonOption[] => {
  if (!Array.isArray(devices)) return [];
  return devices
    .map((device) => {
      const deviceCategoryValue = device?.device_category;
      const deviceCategoryValueLowerCase = deviceCategoryValue?.toLowerCase();
      const deviceCategoryName =
        DEVICE_CATEGORY_NAME_MAP[deviceCategoryValueLowerCase] || device?.device_category_display;
      const subDevices = device?.categroup_devices?.map((subDevice: any) => {
        const deviceNameMap = DEVICE_NAME_MAP_MAP[deviceCategoryValueLowerCase];
        const deviceValue = subDevice?.device;
        const deviceValueLowerCase = deviceValue?.toLowerCase();
        const deviceName = deviceNameMap?.[deviceValueLowerCase] || subDevice?.device_display;
        return { value: deviceValue, text: deviceName };
      });
      return {
        value: deviceCategoryValue,
        text: deviceCategoryName,
        children: subDevices || [],
      };
    })
    .sort((a, b) => {
      const aIdx = DEVICE_CATEGORIES_SORT_KEYS.indexOf(a?.value?.toLowerCase());
      const bIdx = DEVICE_CATEGORIES_SORT_KEYS.indexOf(b?.value?.toLowerCase());
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
};

const flatMapDevices = (devices?: ICommonOption[]): Omit<ICommonOption, 'children'>[] => {
  if (!Array.isArray(devices)) return [];
  return devices.flatMap((device) => {
    const subDevices = device?.children;
    return Array.isArray(subDevices) ? subDevices : [];
  });
};

export default function useGSDOptions() {
  const [loading, setLoading] = useState(true);

  // segment的options集合
  const [segments, setSegments] = useState<Omit<ICommonOption, 'children'>[]>([]);
  // device的options集合（级联）
  const [devices, setDevices] = useState<ICommonOption[]>([]);
  // device的options集合（扁平化）
  const devicesFlatten = useMemo<Omit<ICommonOption, 'children'>[]>(() => {
    return flatMapDevices(devices);
  }, [devices]);

  // combine游戏专用
  const combineDevices = useMemo<ICommonOption[]>(() => {
    if (!Array.isArray(devices)) return [];
    const pcDevice = {
      value: 'PC',
      text: 'PC',
      textEN: 'PC',
      textCN: 'PC',
      children: [
        {
          value: 'PC',
          text: 'PC',
          textEN: 'PC',
          textCN: 'PC',
        },
      ],
    };
    return [pcDevice, ...devices];
  }, [devices]);

  // combine游戏专用
  const combineDevicesFlatten = useMemo(() => {
    return flatMapDevices(combineDevices);
  }, [combineDevices]);

  // 获取全局缓存获取
  const gsdDeviceSegment = useQueryDefinition('gsdDeviceSegment');

  useAsyncEffect(async () => {
    try {
      setLoading(true);
      // const res = await fetchGSDOptions();
      // const resData = res?.data;
      const segmentsTmp = formatSegmentOptions(gsdDeviceSegment?.segments);
      const devicesTmp = formatDeviceOptions(gsdDeviceSegment?.devices);
      setSegments(segmentsTmp);
      setDevices(devicesTmp);
    } catch (exception) {
      setSegments([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [gsdDeviceSegment]);

  return { loading, segments, devices, devicesFlatten, combineDevices, combineDevicesFlatten };
}
